import React, { createContext, useContext, useState } from 'react'
import { TagModal } from './TagModal'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { ITag } from 'constants/interfaces'
import { useDispatch } from 'react-redux'
import { deleteTag } from 'redux/actions/config'
import { AppDispatch } from 'config/redux'
import { useToast } from '@chakra-ui/core'
import useLocaleText from 'components/useLocaleText'
interface TagModalsContextType {
  openTagModal: (tag?: ITag) => void
  requestCloseTagModal: () => void
  requestDeleteTag: (tag: ITag) => void
}

const TagModalsContext = createContext<TagModalsContextType | null>(null)

export const useTagModals = () => {
  const ctx = useContext(TagModalsContext)
  if (!ctx) throw new Error('useTagModals must be used within TagModalsProvider')
  return ctx
}

export const TagModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()

  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('no')
  const admin_yes_cancel = useLocaleText('yes')
  const admin_delete_tag_confirm = useLocaleText('admin_delete_tag_confirm')

  const [tagModalOpen, setTagModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  const [selectedTag, setSelectedTag] = useState<ITag | null>(null)
  const [tagToDelete, setTagToDelete] = useState<ITag | null>(null)
  const [isDeleteTagLoading, setIsDeleteTagLoading] = useState(false)

  const openTagModal = (tag?: ITag) => {
    setSelectedTag(tag || null)
    setTagModalOpen(true)
  }

  const onClose = () => {
    setTagModalOpen(false)
    setConfirmationOpen(false)
    setSelectedTag(null)
  }

  const requestCloseTagModal = () => {
    setConfirmationOpen(true)
  }

  const confirmClose = () => {
    setTagModalOpen(false)
    setSelectedTag(null)
    setConfirmationOpen(false)
  }

  const cancelClose = () => setConfirmationOpen(false)

  const requestDeleteTag = (tag: ITag) => {
    setTagToDelete(tag)
    setDeleteConfirmationOpen(true)
  }

  const confirmDeleteTag = async () => {
    if (tagToDelete) {
      try {
        setIsDeleteTagLoading(true)
        await dispatch(deleteTag(tagToDelete))
        setDeleteConfirmationOpen(false)
        setTagToDelete(null)
        toast({
          title: 'Tag deleted',
          description: 'Tag deleted successfully',
          status: 'success',
          position: 'top-right',
        })
      } catch (error) {
        console.error('[confirmDeleteTag] Error:', error)
        toast({
          title: 'Error',
          description: 'Failed to delete tag',
          status: 'error',
          position: 'top-right',
        })
      } finally {
        setIsDeleteTagLoading(false)
      }
    }
  }

  const cancelDeleteTag = () => {
    setDeleteConfirmationOpen(false)
    setTagToDelete(null)
  }

  return (
    <TagModalsContext.Provider value={{ openTagModal, requestCloseTagModal, requestDeleteTag }}>
      {children}

      {tagModalOpen && (
        <TagModal isOpen tag={selectedTag} requestCloseTagModal={requestCloseTagModal} onClose={onClose} />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}

      {deleteConfirmationOpen && tagToDelete && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelDeleteTag}
          onConfirm={confirmDeleteTag}
          isLoading={isDeleteTagLoading}
          title={admin_delete_tag_confirm}
          description={`${admin_delete_tag_confirm} "${tagToDelete.name}"?`}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}
    </TagModalsContext.Provider>
  )
}
